import React from 'react'
import { Checkbox, Typography, message } from 'antd'
import { CheckboxChangeEvent } from 'antd/es/checkbox'
import { useNavigate } from 'react-router-dom'
import { AxiosError } from 'axios'
import { sellerApi } from 'api/seller'
import { IDeliveryWays, IOwnDelivery, TVariantSeller } from 'models/seller'
import { superAdminRoutes } from 'shared/constants/routes'
import { showServerError } from 'shared/utils/showServerError'
import { SectionTitle } from 'components/ui'
import { OwnDeliveryForm } from './OwnDeliveryForm'
import styles from './SellerDelivery.module.scss'

interface IDeliveryItemProps {
  label: string
  value: string
  checked: boolean
  onChange: (e: CheckboxChangeEvent) => void
}

interface ISellerDeliveryProps {
  type: TVariantSeller
  sellerId: string
}

// Яндекс экспресс не выпилен, закомменнтирован, вдруг пригодится
const DELIVERY_KEYS = ['pickup', 'delivery_to_point', 'courier'] as Array<keyof IDeliveryWays>

const getDeliveryWaysLabel = (key: keyof IDeliveryWays) => {
  switch (key) {
  case 'pickup':
    return 'Самовывоз'
  case 'delivery_to_point':
    return 'Доставка до пункта выдачи'
    // case 'yandex_express':
    //   return 'Яндекс экспресс'
  case 'courier':
    return 'Доставка курьером'
  default:
    return 'Неизвестно'
  }
}

const DeliveryItem: React.FC<IDeliveryItemProps> = ({
  label,
  value,
  checked,
  onChange
}) => {
  return (
    <div className={styles.deliveryWrapper}>
      <Checkbox checked={checked} value={value} onChange={onChange}>
        <Typography.Text>{label}</Typography.Text>
      </Checkbox>
    </div>
  )
}

export const SellerDelivery: React.FC<ISellerDeliveryProps> = ({
  type,
  sellerId
}) => {
  const navigate = useNavigate()

  const [isLoading, setIsLoading] = React.useState(true)
  const [isError, setIsError] = React.useState(false)
  const [deliveryWays, setDeliveryWays] = React.useState<IDeliveryWays | null>(
    null
  )
  const [ownDelivery, setOwnDelivery] = React.useState<IOwnDelivery | null>(null)
  const [isOwnDeliveryFormOpen, setIsOwnDeliveryFormOpen] = React.useState(false)

  React.useEffect(() => {
    const getDeliveryWays = async () => {
      try {
        message.loading('Загрузка способов доставки')
        const response = await sellerApi.getDeliveryWays(sellerId)
        // const ways = {
        //   ...response.data,
        //   yandex_express: response.data.yandex_express || false
        // }
        const ways = {
          ...response.data
        }
        setDeliveryWays(ways)
      } catch (e) {
        const error = e as AxiosError
        if (
          (error.response?.status === 500 ||
            error.response?.status === 400 ||
            error.response?.status === 404) &&
          type === 'seller'
        ) {
          return navigate(superAdminRoutes.SELLERS)
        }
        showServerError(e)
        setIsError(true)
      } finally {
        message.destroy()
        setIsLoading(false)
      }
    }
    getDeliveryWays()
  }, [])

  // Отдельным запросом: без него остальные способы доставки всё равно настраиваются
  React.useEffect(() => {
    sellerApi
      .getOwnDelivery(sellerId)
      .then(({ data }) => setOwnDelivery(data))
      .catch(showServerError)
  }, [sellerId])

  const changeDeliveryWays = async (
    key: keyof IDeliveryWays,
    value: boolean
  ) => {
    try {
      message.loading('Обновление данных')
      const newDeliveryWays = {
        ...(deliveryWays as IDeliveryWays),
        [key]: value
      }
      await sellerApi.changeDeliveryWays({
        ...newDeliveryWays,
        seller_id: sellerId
      })
      setDeliveryWays(newDeliveryWays)
      message.destroy()
      message.success('Данные успешно обновлены')
    } catch (e) {
      message.destroy()
      showServerError(e)
    }
  }

  const onChange = async (e: CheckboxChangeEvent) => {
    changeDeliveryWays(e.target.value as keyof IDeliveryWays, e.target.checked)
  }

  // Включается кнопкой в форме, когда заполнены лимиты и зона; выключается сразу
  const onOwnDeliveryChange = async (e: CheckboxChangeEvent) => {
    if (e.target.checked) {
      setIsOwnDeliveryFormOpen(true)
      return
    }
    if (!ownDelivery?.enabled) {
      setIsOwnDeliveryFormOpen(false)
      return
    }
    try {
      message.loading('Обновление данных')
      const { data } = await sellerApi.updateOwnDelivery({
        seller_id: sellerId,
        enabled: false,
        settings: null
      })
      setOwnDelivery(data)
      setIsOwnDeliveryFormOpen(false)
      message.destroy()
      message.success('Данные успешно обновлены')
    } catch (e) {
      message.destroy()
      showServerError(e)
    }
  }

  const onOwnDeliverySaved = (data: IOwnDelivery) => {
    setOwnDelivery(data)
    setIsOwnDeliveryFormOpen(false)
  }

  const isOwnDeliveryShown = Boolean(ownDelivery?.enabled || isOwnDeliveryFormOpen)

  return (
    <>
      {isLoading || isError ? null : (
        <div style={{ paddingBottom: 80 }}>
          <SectionTitle level={2} className={styles.title}>
            Способы доставки
          </SectionTitle>
          <div className={styles.deliveryList}>
            {deliveryWays &&
              DELIVERY_KEYS.filter((key) => key in deliveryWays).map((key) => (
                <DeliveryItem
                  key={key}
                  label={getDeliveryWaysLabel(key)}
                  value={key}
                  checked={Boolean(deliveryWays[key])}
                  onChange={onChange}
                />
              ))}
            {ownDelivery && (
              <DeliveryItem
                label='Самостоятельная доставка'
                value='own_delivery'
                checked={isOwnDeliveryShown}
                onChange={onOwnDeliveryChange}
              />
            )}
          </div>
          {ownDelivery && isOwnDeliveryShown && (
            <OwnDeliveryForm
              sellerId={sellerId}
              ownDelivery={ownDelivery}
              onSaved={onOwnDeliverySaved}
              onCancel={() => setIsOwnDeliveryFormOpen(false)}
            />
          )}
        </div>
      )}
    </>
  )
}
